import {forwardRef, useMemo} from "react";
import {useUser} from "../../../../misc/Permission";
import {PDF} from "../../../../misc/pdf/parts/PDF";
import {ClientInfoContextProvider, PDFProjectContext} from "../../shopdrawing/openings/OpeningsPDF";
import {Company} from "../../../../api/Companies";
import {Project} from "../../../../api/Projects";
import {useReadyCheck} from "../../../../misc/pdf/UseReadyCheck";
import {CoverPage} from "../../../../misc/pdf/parts/CoverPage";
import {HwScheduleFinishesPage} from "./HwScheduleFinishesPage";
import {HardwareGroupWithContent} from "../../../../api/Hardware";
import {PDFInfo} from "../../../../misc/pdf/PrintManager";
import {Page} from "../../../../misc/pdf/parts/Page";
import {DataSplitInfo, PageRenderProvider} from "../../../../misc/pdf/parts/PageContext";
import {css} from "@emotion/css";
import {grey} from "@mui/material/colors";
import * as React from "react";
import {rebuildSplitPrefItems, SourceItem} from "../../../../misc/pdf/parts/RebuildSplitPref";
import {fixFinishName} from "../../../../api/Products";
import {HardwarePageItem} from "./CustomizeHardwareSchedule";
import {Contact} from "../../../../api/Contacts";
import {hasPreambleContent, preambleToBlocks} from "../../../../misc/richtext/preambleContent";
import {ContainerPage} from "../../../../misc/pdf/parts/ContainerPage";
import {TitleTextBlock} from "../../../../misc/pdf/parts/TitleTextBlock";
import {ViewMode} from "./HardwareViewMode";
import {groupByPhase, phaseLabel} from "../../../../misc/Phase";
import {scopeHardwareToOpenings} from "../../shopdrawing/hardware/WorkingHardware";

export function hardwareSchedulePDF(props: {
        lastSubmittal: string | null;
        project: Project;
        client?: Company;
        clientContact?: Contact;
        hardware: HardwareGroupWithContent[]
        pages: HardwarePageItem[];
        preamble: string
        viewMode: ViewMode;
        splitByPhase: boolean;
    }): PDFInfo {

    const products: number[] = [];
    const finishObj: {[k: string]: string} = {};
    const mfgObj: {[k: string]: string} = {};

    props.hardware.map(h => h.hardware.map(item => {
        if(!products.includes(item.product)) {
            products.push(item.product);
        }

        if(fixFinishName(item.finish) && fixFinishName(item.finishName)) {
            finishObj[item.finish] = item.finishName;
        }

        if(item.manufacturerShortName && item.manufacturerName) {
            mfgObj[item.manufacturerShortName] = item.manufacturerName
        }
    }))

    const finishes = Object.keys(finishObj).map(f => {
        return {
            key: f,
            value: finishObj[f]
        }
    })

    const manufacturers = Object.keys(mfgObj).map(m => {
        return {
            key: m,
            value: mfgObj[m]
        }
    });

    return {
        name: "hardware-schedule.pdf",
        render: async onReady => {
            return <HardwareSchedulePDF
                finishes={finishes}
                manufacturers={manufacturers}
                hardware={props.hardware}
                preamble={props.preamble}
                project={props.project}
                lastSubmittal={props.lastSubmittal}
                client={props.client}
                clientContact={props.clientContact}
                onReady={onReady}
                pages={props.pages}
                viewMode={props.viewMode}
                splitByPhase={props.splitByPhase}
            />
        },
        serverInfo: {
            submissionForProject: props.project.id,
            submissionType: "hardware-template",
            appendCatalogPagesForProducts: props.pages.includes("cutsheets") ? products : [],
        }
    }
}

export type KeyValue = {
    key: string;
    value: string;
}



type RenderKey = "phase" | "header" | "hardware"| "opening" | "doorName" | "notes"

export const HardwareSchedulePDF = forwardRef(function (props: {
    project: Project;

    preamble: string;
    manufacturers: KeyValue[];
    finishes: KeyValue[];
    hardware: HardwareGroupWithContent[];
    pages: HardwarePageItem[];
    viewMode: ViewMode;
    splitByPhase: boolean;

    client?: Company;
    clientContact?: Contact;
    lastSubmittal: string|null;
    onReady(): void;
}, ref) {
    const usr = useUser();
    const ready = useReadyCheck(props.onReady);
    ready.require(!!usr);

    const preamble = useMemo(() => preambleToBlocks(props.preamble || ""), [props.preamble])

    const groupNumber = useMemo(() => new Map<HardwareGroupWithContent, string>(), []);
    const phaseHeading = useMemo(() => new Map<HardwareGroupWithContent, string>(), []);

    // one list per page-section: each phase starts on a new page
    const sectionRows = useMemo(() => {
        let list: SourceItem<HardwareGroupWithContent, RenderKey>[] = [];
        const out = [list];

        groupNumber.clear();
        phaseHeading.clear();

        const sections = props.splitByPhase
            ? groupByPhase(splitGroupsByPhase(props.hardware), g => g.openings[0]?.phase || "")
            : [{phase: "", items: props.hardware}];

        const groups = sections.flatMap(section => autoSplit(section.items).map((h, index) => {
            if(props.splitByPhase && index === 0) {
                phaseHeading.set(h, phaseLabel(section.phase));
            }

            return h;
        }));

        groups.map((h, hIndex) => {
            groupNumber.set(h, "#" + (hIndex + 1));

            if(phaseHeading.has(h)) {
                if(list.length > 0) {
                    list = [];
                    out.push(list);
                }

                list.push({
                    type: "phase",
                    index: 0,
                    obj: h,
                })
            }

            list.push({
                type: "header",
                index: 0,
                obj: h,
            })

            h.openings.map((o, index) => {
                list.push({
                    type: "opening",
                    index: index,
                    obj: h,
                })
            })

            list.push({
                type: "doorName",
                index: 0,
                obj: h,
            })

            h.hardware.map((o, index) => {
                list.push({
                    type: "hardware",
                    index: index,
                    obj: h,
                })
            })

            if(h.notes) {
                list.push({
                    type: "notes",
                    index: 0,
                    obj: h,
                })
            }
        })

        return out
    }, [props.hardware, props.splitByPhase, groupNumber, phaseHeading])

    return (
        <div ref={ref as any}>
            <PDF>
                <PDFProjectContext.Provider value={props.project}>
                    <ClientInfoContextProvider client={props.client} clientContact={props.clientContact}>
                        <CoverPage
                            title={props.pages.includes("schedule") ? "HARDWARE SCHEDULE SUBMITTAL" : "HARDWARE CUTSHEET SUBMITTAL"}
                            lastSubmittal={props.lastSubmittal}
                            portrait
                        />

                        {hasPreambleContent(props.preamble || "") && <PageRenderProvider data={preamble} onReady={ready.section()}>
                            {info =>
                                <ContainerPage orientation="portrait">
                                    <TitleTextBlock title="PREAMBLE">
                                        {info.rows.map((d, index) => <div key={index.toString()} dangerouslySetInnerHTML={{__html: d}} />)}
                                    </TitleTextBlock>
                                </ContainerPage>}
                        </PageRenderProvider>}

                        {props.pages.includes("finishes") && <HwScheduleFinishesPage manufacturers={props.manufacturers} finishes={props.finishes} />}

                        {props.pages.includes("schedule") && sectionRows.map((rows, sectionIndex) => <PageRenderProvider key={sectionIndex} data={rows}
                                                                                 onReady={ready.section()} preferCombinedKey={row => row.obj.id.toString()}
                                                                                 debugKey={"hw-schedule-" + sectionIndex}
                        >
                            {(info: DataSplitInfo<SourceItem<HardwareGroupWithContent, RenderKey>>) => (
                                <Page orientation="portrait">
                                    <div style={{display: "flex", flexDirection: "column", alignItems: "stretch", width: "100%"}}>
                                    {rebuildSplitPrefItems<HardwareGroupWithContent, RenderKey>(info.rows)
                                        .map((r, index) => {
                                            return (
                                                <React.Fragment key={"frag-" + index.toString()}>
                                                    {index !== 0 && <div style={{height: 20}} />}
                                                    {r.phase && <div style={{fontWeight: "bold", fontSize: "1.3rem", paddingTop: 10, borderBottom: "2px solid black"}}>{phaseHeading.get(r.obj)}</div>}
                                                    {r.header && <div style={{fontWeight: "bold", fontSize: "1.1rem", paddingTop: 10, paddingBottom: 10}}>Heading {props.viewMode === "by-opening" ? groupNumber.get(r.obj) : r.obj.name}</div>}
                                                    <table className={pdfTable} style={{width: "100%"}} key={"tbl-" + index.toString()}>
                                                        {r.opening && r.opening.slice(r.obj.openings)
                                                            .map(o => {
                                                                return (
                                                                    <tr key={"opening" + o.id}>
                                                                        <td colSpan={6} style={{fontWeight: "bold"}}>
                                                                            <div style={{display: "flex", flexWrap: "nowrap", gap: 8,}}>
                                                                                <div style={{flex: 1}}>{o.descriptiveName}</div>
                                                                                <div>
                                                                                    <div style={{
                                                                                        display: "flex",
                                                                                        gap: 4,
                                                                                    }}>
                                                                                        {o.degreesOfOpeningActive && <div>{o.degreesOfOpeningInactive ? "Active: " : ""}{o.degreesOfOpeningActive}°</div>}
                                                                                        {o.degreesOfOpeningInactive && <div>Inactive: {o.degreesOfOpeningInactive}°</div>}
                                                                                    </div>
                                                                                </div>
                                                                                <div>
                                                                                    {o.handing}
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            })}
                                                        {r.doorName && <tr key="door">
                                                            <td colSpan={6} style={{fontWeight: "bold"}}>
                                                                {r.obj.openings.length > 0 && r.obj.openings[0].doorName}
                                                            </td>
                                                        </tr>}

                                                        {r.hardware && r.hardware.slice(r.obj.hardware)
                                                            .map(h => {
                                                                const list = h.calcByDimension && h.calcByDimension.length > 0 ? h.calcByDimension : [{
                                                                    dimension: "",
                                                                    activeQty: h.calcTotalQty,
                                                                    inactiveQty: 0,
                                                                    commonQty: 0,
                                                                    productCode: h.productCode,
                                                                }];

                                                                return list.map(l => {
                                                                    return (
                                                                        <tr key={h.id.toString() + l.dimension}>
                                                                            <td style={{width: 50}}>{l.activeQty + l.inactiveQty + l.commonQty}</td>
                                                                            <td style={{width: 250}}>{h.name}{h.customerSupplied ? " by " + h.supplierCategoryName : ""}</td>
                                                                            <td>{l.productCode}</td>
                                                                            <td style={{width: 100}}>{l.dimension}</td>
                                                                            <td style={{width: 50}}>{fixFinishName(h.finish)}</td>
                                                                            <td style={{width: 50}}>{h.manufacturerShortName}</td>
                                                                        </tr>
                                                                    )
                                                                })
                                                            })}
                                                    </table>

                                                    {r.notes && <div>
                                                        <p style={{whiteSpace: "pre-wrap", lineHeight: "1.4rem", padding: "0.5rem", paddingTop: "0.25rem"}}>
                                                            {r.obj.notes}
                                                        </p>
                                                    </div>}
                                                </React.Fragment>
                                            )
                                    })}
                                    </div>
                                </Page>
                            )}
                        </PageRenderProvider>)}
                    </ClientInfoContextProvider>
                </PDFProjectContext.Provider>
            </PDF>
        </div>
    )
});

// a group with openings in more than one phase becomes one copy per phase, each with only that phase's openings
function splitGroupsByPhase(list: HardwareGroupWithContent[]) {
    return list.flatMap(group => {
        const sections = groupByPhase(group.openings, o => o.phase);
        if(sections.length <= 1) return [group];

        return sections.map((section, index) => Object.assign(scopeHardwareToOpenings(group, section.items), {
            id: -(group.id + (index + 1) / 1000), // fractional id to preserve uniqueness, but cause errors if someone tries to submit it
        }));
    });
}

function autoSplit(list: HardwareGroupWithContent[]) {
    let out: HardwareGroupWithContent[] = [];

    list.forEach(itm => {
        const openingMap = new Map<string, number[]>();

        itm.openings.forEach(op => {
            const details: string[] = [];

            for(let hw of itm.hardware) {
                if(!hw.calcByDimension) continue;

                for(let byDim of hw.calcByDimension) {
                    if(!byDim.openingDetail) continue;

                    byDim.openingDetail.forEach(opDetail => {
                        if(opDetail.opening !== op.id) return;
                        details.push(JSON.stringify([byDim.productCode, opDetail.subType, opDetail.qty]));
                    })
                }
            }

            details.sort();
            const key = JSON.stringify(details)

            if(!openingMap.has(key)) {
                openingMap.set(key, []);
            }

            openingMap.get(key)!.push(op.id);
        })

        if(openingMap.size === 1) {
            out.push(itm);
            return;
        }

        const batch: HardwareGroupWithContent[] = [];

        openingMap.forEach(group => {
            batch.push({
                ...itm,
                openings: itm.openings.filter(op => group.includes(op.id)),
                hardware: itm.hardware.map(hw => {
                    hw = {...hw};
                    if(!hw.calcByDimension) return hw;
                    hw.calcByDimension = hw.calcByDimension.filter(byDim => {
                        if(!byDim.openingDetail) return true;
                        if(byDim.openingDetail.find(opd => group.includes(opd.opening))) return true;
                        return false;
                    });

                    return hw;
                })
            })
        })

        batch.sort((a, b) => {
            const aMin = Math.min(...a.openings.map(op => op.seqNumber));
            const bMin = Math.min(...b.openings.map(op => op.seqNumber));
            return aMin - bMin;
        })

        batch.forEach((v, index) => {
            v.name = v.name + "-" + alpha[index]
            out.push(v);
        })
    })

    return out;
}

const alpha = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export const pdfTable = css({
    width: "100%",
    borderSpacing: 0,
    borderCollapse: "collapse",

    "& > * > * > *": { // all cells
        paddingLeft: 4,
        paddingRight: 4,
    },

    "& > tbody > * > *": {
        border: "1px solid " + grey["700"],
    },

    "& .pdf-header": {
        fontWeight: "bold",
        fontSize: "0.8rem",
    },
})